import { withDatasourceCheck } from "@sitecore-jss/sitecore-jss-nextjs";
import { ComponentProps } from "lib/component-props";
import Image from "next/image";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useEffect } from "react";
import { Disclosure } from "@headlessui/react";
import { ChevronUpIcon } from "@heroicons/react/20/solid";

type NationalStrategyProps = ComponentProps & {
  fields: any;
  locale: any;
  lang: string;
};

gsap.registerPlugin(ScrollTrigger);

const NationalStrategy = (props: NationalStrategyProps): JSX.Element => {
  const { title, childItems }: any = props.fields;
  useEffect(() => {
    gsap.set(".animate-top", {
      opacity: 0,
      y: 100,
    });
    ScrollTrigger.batch(".animate-top", {
      onEnter: (batch) =>
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          stagger: { each: 0.7 },
          overwrite: true,
        }),
      onEnterBack: (batch) =>
        gsap.to(batch, { opacity: 1, y: 0, stagger: 0.2, overwrite: true }),
    });
  });
  const DesktopDesign = () => {
    return (
      <div
        className={`  w-full overflow-hidden relative lg:py-36 py-20  bg-[#3a6481] ${
          props?.locale == "ar" ? "rtl" : ""
        }`}
      >
        <div className=" absolute -top-28"></div>
        <div
          className={` duration-500 ease-in  absolute bottom-0   h-full w-full`}
          style={{
            mixBlendMode: "multiply",
            height: "100%",
            background:
              "transparent linear-gradient(180deg, #283E4CDB 66%, #152D3C 91%, #0F2837 100%) 0% 0% no-repeat ",
          }}
        />
        <div className=" absolute h-full w-full top-0  ">
          <Image
            src={props?.fields?.backgroundImage?.value?.src}
            alt={(props?.fields?.backgroundImage?.value?.alt ?? "").trim() || (title?.value ?? "").trim() || "National strategy background"}
            width={props?.fields?.backgroundImage?.value?.width}
            height={props?.fields?.backgroundImage?.value?.height}
            layout="fill"
            objectFit="cover"
            className="-z-1"
            priority
          />
        </div>
        <div className="responsive  relative z-20 flex flex-col">
          <h4 className="2xl:text-[28px] text-[20px] font-bold leading-8 text-white mb-[30px] animate-top text-center">
            {title?.value}
          </h4>

          <div className="grid xl:grid-cols-3 grid-cols-1 xl:gap-32 gap-20 justify-between">
            {childItems.map((section: any, index: number) => {
              return (
                <div key={index} className="col-span-1 ">
                  <h5 className="2xl:text-[22px] text-[18px] leading-8 text-white animate-top">
                    {section?.fields?.Title.value}
                  </h5>
                  <div className="2xl:mt-6 mt-2 2xl:mb-12 mb-6 animate-top">
                    <svg
                      width="217"
                      height="6"
                      viewBox="0 0 217 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden={true}
                      focusable="false"
                    >
                      <rect width="217" height="6" fill="#FF9619" />
                    </svg>
                  </div>
                  <ul
                    className={`flex flex-col gap-8 animate-top ${
                      props?.locale == props?.lang ? "pr-4" : "pl-4"
                    }`}
                    style={{ listStyleType: "square" }}
                  >
                    {section?.fields?.InnerItems?.map(
                      (points: any, index: number) => {
                        return (
                          <li
                            key={index}
                            className=" marker:text-[#FF9619] text-white 2xl:text-[18px] text-[14px] leading-6 font-normal"
                          >
                            {points.fields?.Title?.value}
                          </li>
                        );
                      }
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };
  const MobileDesign = () => {
    return (
      <div
        className={`  w-full overflow-hidden relative   py-10  ${
          props?.locale == "ar" ? "rtl" : ""
        }`}
      >
        <div className=" absolute -top-28"></div>

        <div className="  relative z-20 flex flex-col">
          <h4 className="responsive 2xl:text-[28px] text-[20px] font-bold leading-8 text-brand-50 mb-[30px] animate-top ">
            {title?.value}
          </h4>
          <div className="grid xl:grid-cols-3 grid-cols-1  justify-between">
            {childItems.map((section: any, index: number) => {
              return (
                <div key={index} className="bg-[#F1F1F1] ">
                  <Disclosure>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex relative w-full justify-between  items-center   min-h-[120px]">
                          <div
                            className={` duration-500 ease-in  absolute bottom-0 z-10   w-full h-full bg-black opacity-50`}
                          />
                          <div className=" absolute h-full w-full top-0  ">
                            <Image
                              src={
                                section?.fields?.MobileBackgroundImage?.value
                                  ?.src
                              }
                              alt={(section?.fields?.MobileBackgroundImage?.value?.alt ?? "").trim() || (section?.fields?.Title?.value ?? "").trim() || "National strategy section image"}
                              width={
                                section?.fields?.MobileBackgroundImage?.value
                                  ?.width
                              }
                              height={
                                section?.fields?.MobileBackgroundImage?.value
                                  ?.height
                              }
                              layout="fill"
                              objectFit="cover"
                              className="-z-1"
                              priority
                            />
                          </div>
                          <div className="flex items-center relative z-20">
                            <svg
                              className={`rotate-90 ${
                                props?.locale == "ar"
                                  ? "-mr-[30px]"
                                  : "-ml-[30px]"
                              }`}
                              width="100"
                              height="4"
                              viewBox="0 0 100 6"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden={true}
                              focusable="false"
                            >
                              <rect width="100" height="4" fill="#FF9619" />
                            </svg>

                            <h2 className="  text-[14px] leading-8  text-white animate-top relative z-20">
                              {section?.fields?.Title.value}
                            </h2>
                          </div>
                          <ChevronUpIcon
                            aria-hidden={true}
                            className={`${
                              open ? "rotate-180 transform" : ""
                            } h-5 w-5 text-[#FF9619]   !opacity-100 relative z-20 ${
                              props?.locale == "ar" ? "ml-4" : "mr-4"
                            } `}
                          />
                        </Disclosure.Button>
                        <Disclosure.Panel className=" z-30 relative -top-[40px] px-4 pt-6 pb-6 text-sm bg-[#F1F1F1]">
                          <ul
                            className={`flex flex-col gap-4 animate-top ${
                              props?.locale == props?.lang ? "pr-4" : "pl-4"
                            }`}
                            style={{ listStyleType: "square" }}
                          >
                            {section?.fields?.InnerItems?.map(
                              (points: any, index: number) => {
                                return (
                                  <li
                                    key={index}
                                    className=" marker:text-[#FF9619] text-brand-50 2xl:text-[18px] text-[14px] leading-6 font-normal"
                                  >
                                    {points.fields?.Title?.value}
                                  </li>
                                );
                              }
                            )}
                          </ul>
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };
  return (
    <section>
      <div className="md:block hidden">{DesktopDesign()}</div>
      <div className="md:hidden block">{MobileDesign()}</div>
    </section>
  );
};

export default withDatasourceCheck()<NationalStrategyProps>(NationalStrategy);
